import { theme, Tooltip } from "antd";
import { CSSProperties } from "react";
import { AttributeField } from "src/components/AttributeField";
import { Flex } from "src/components/Flex";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { attributeToTitle } from "src/utils/strings";
import { HeaderAttribute } from "../HeaderAttributes";
import Link from "next/link";
import { ExportOutlined } from "@ant-design/icons";
import { MetadataTag, IndicatorType } from "src/components/MetadataTag";

export const HeaderPanel = (props: {
  entity: any;
  attributes: HeaderAttribute[];
  icon?: React.ReactElement;
  label: string;
  entityType?: string;
}) => {
  const { token } = theme.useToken();
  return (
    <Flex
      style={{
        paddingRight: 40,
        paddingBottom: 24,
        color: token.colorText,
      }}
    >
      <div style={headerPropStyle}>
        <div style={{ width: 40, paddingTop: 2 }}>
          {props.icon || (
            <IntegrationIcon
              source={props.entity.properties?.integration}
              size="large"
            />
          )}
        </div>
        <div style={{ width: "100%", paddingTop: 8 }}>
          <div style={{ fontWeight: 600, maxWidth: "90%" }} className="trunc">
            {props.label || "N/A"}
            {props.entity.properties.sourceUrl && (
              <Tooltip title="Go to source">
                <Link
                  href={props.entity.properties.sourceUrl}
                  style={{ marginLeft: 5 }}
                  target="_blank"
                >
                  <ExportOutlined />
                </Link>
              </Tooltip>
            )}
          </div>
          <div style={labelStyle}>
            {attributeToTitle(props.entityType || props.entity.labels[0])}
          </div>
        </div>
      </div>
      <Flex
        gap={20}
        style={{
          maxWidth: "70%",
          justifyContent: "flex-end",
          overflow: "hidden",
          paddingTop: 16,
        }}
      >
        {props.attributes.map((attribute) => (
          <EntityAttribute
            key={attribute.value}
            {...attribute}
            entity={props.entity}
          />
        ))}
      </Flex>
    </Flex>
  );
};

const EntityAttribute = ({
  value,
  child,
  entity,
  label,
}: HeaderAttribute & { entity: any }) => {
  // Special handling for status, type, and priority
  if (value === "status") {
    return (
      <div style={{ minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontWeight: 500 }} className="trunc">
          <MetadataTag
            metadata={entity.properties[value]}
            type={IndicatorType.status}
            size="medium"
            showLabel
          />
        </div>
        <div style={labelStyle} className="trunc">
          {attributeToTitle(label || value)}
        </div>
      </div>
    );
  }

  if (value === "issueType") {
    return (
      <div style={{ minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontWeight: 500 }} className="trunc">
          <MetadataTag
            metadata={entity.properties[value]}
            type={IndicatorType.issuetype}
            size="medium"
            showLabel
          />
        </div>
        <div style={labelStyle} className="trunc">
          {attributeToTitle(label || value)}
        </div>
      </div>
    );
  }

  if (value === "priority") {
    return (
      <div style={{ minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontWeight: 500 }} className="trunc">
          <MetadataTag
            metadata={entity.properties[value]}
            type={IndicatorType.priority}
            size="medium"
            showLabel
          />
        </div>
        <div style={labelStyle} className="trunc">
          {attributeToTitle(label || value)}
        </div>
      </div>
    );
  }

  return (
    <Tooltip
      title={
        <div>
          {value}
          <br />
          {entity.properties[value] || "n/a"}
        </div>
      }
    >
      <div style={{ minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontWeight: 500 }} className="trunc">
          {child ? child : <AttributeField value={entity.properties[value]} />}
        </div>
        <div style={labelStyle} className="trunc">
          {attributeToTitle(label || value)}
        </div>
      </div>
    </Tooltip>
  );
};

const labelStyle: CSSProperties = {
  fontSize: 10,
  paddingTop: 5,
  textTransform: "uppercase",
};

const headerPropStyle: CSSProperties = {
  display: "flex",
  flex: 2,
  gap: 10,
  lineHeight: "normal",
  minWidth: 0,
  maxWidth: "100%",
  paddingTop: 20,
  paddingLeft: 20,
  height: 72,
};
